import React from 'react';
import {
  Zap, Shield, Sparkles, Star, Heart, CheckCircle, ArrowRight,
  Globe, Lock, Rocket, Target, Users, BarChart3, Clock, Award,
  Lightbulb, Layers, Code, MessageCircle, TrendingUp, Settings,
  Mail, Phone, Calendar, Database, FileText, Search, Bell, Gift,
} from 'lucide-react';

const HEADING_FONT = 'Manrope, sans-serif';
const BODY_FONT = 'Inter, sans-serif';

// Map of commonly used icon names to lucide-react components
const ICON_MAP = {
  Zap, Shield, Sparkles, Star, Heart, CheckCircle, ArrowRight,
  Globe, Lock, Rocket, Target, Users, BarChart3, Clock, Award,
  Lightbulb, Layers, Code, MessageCircle, TrendingUp, Settings,
  Mail, Phone, Calendar, Database, FileText, Search, Bell, Gift,
};

function getIcon(iconName) {
  return ICON_MAP[iconName] || Zap;
}

/**
 * Features Block Renderer
 * Variants: grid_3, grid_2, alternating, icon_list
 */
export default function FeaturesRenderer({ props, variant }) {
  const {
    headline = 'Features',
    subheadline = '',
    items = [],
  } = props;

  const headerEl = (
    <div className="text-center mb-12">
      <h2
        className="text-3xl sm:text-4xl font-bold text-stone-900 mb-3"
        style={{ fontFamily: HEADING_FONT }}
      >
        {headline}
      </h2>
      {subheadline && (
        <p
          className="text-lg text-stone-500 max-w-2xl mx-auto"
          style={{ fontFamily: BODY_FONT }}
        >
          {subheadline}
        </p>
      )}
    </div>
  );

  // Grid 3 column variant
  if (variant === 'grid_3') {
    return (
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          {headerEl}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((item, i) => {
              const Icon = getIcon(item.icon);
              return (
                <div key={i} className="text-center p-6 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-stone-900 text-white mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3
                    className="text-lg font-semibold text-stone-900 mb-2"
                    style={{ fontFamily: HEADING_FONT }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="text-sm text-stone-500 leading-relaxed"
                    style={{ fontFamily: BODY_FONT }}
                  >
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  // Grid 2 column variant
  if (variant === 'grid_2') {
    return (
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          {headerEl}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {items.map((item, i) => {
              const Icon = getIcon(item.icon);
              return (
                <div key={i} className="flex gap-4 p-6 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-stone-900 text-white flex items-center justify-center">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3
                      className="text-lg font-semibold text-stone-900 mb-1"
                      style={{ fontFamily: HEADING_FONT }}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="text-sm text-stone-500 leading-relaxed"
                      style={{ fontFamily: BODY_FONT }}
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  // Alternating left/right rows
  if (variant === 'alternating') {
    return (
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          {headerEl}
          <div className="space-y-16">
            {items.map((item, i) => {
              const Icon = getIcon(item.icon);
              const isReversed = i % 2 === 1;
              return (
                <div
                  key={i}
                  className={`flex flex-col md:flex-row items-center gap-8 ${
                    isReversed ? 'md:flex-row-reverse' : ''
                  }`}
                >
                  <div className="flex-1 flex items-center justify-center">
                    <div className="w-24 h-24 rounded-2xl bg-stone-100 flex items-center justify-center">
                      <Icon className="w-12 h-12 text-stone-700" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3
                      className="text-2xl font-bold text-stone-900 mb-2"
                      style={{ fontFamily: HEADING_FONT }}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="text-base text-stone-500 leading-relaxed"
                      style={{ fontFamily: BODY_FONT }}
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  // Icon list variant (vertical)
  if (variant === 'icon_list') {
    return (
      <section className="py-16 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          {headerEl}
          <div className="space-y-4">
            {items.map((item, i) => {
              const Icon = getIcon(item.icon);
              return (
                <div
                  key={i}
                  className="flex items-start gap-4 p-4 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-stone-900 text-white flex items-center justify-center mt-0.5">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3
                      className="text-base font-semibold text-stone-900 mb-0.5"
                      style={{ fontFamily: HEADING_FONT }}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="text-sm text-stone-500 leading-relaxed"
                      style={{ fontFamily: BODY_FONT }}
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  // Fallback to grid_3
  return (
    <section className="py-16 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        {headerEl}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {items.map((item, i) => {
            const Icon = getIcon(item.icon);
            return (
              <div key={i} className="text-center p-6 rounded-xl bg-stone-50">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-stone-900 text-white mb-4">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-stone-900 mb-2" style={{ fontFamily: HEADING_FONT }}>
                  {item.title}
                </h3>
                <p className="text-sm text-stone-500" style={{ fontFamily: BODY_FONT }}>
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
